import * as React from 'react';
import moment from 'moment';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { ReactComponent as SearchIcon } from 'common/dist/assets/search.svg';

import * as api from '../../../../api';
import * as store from '../../../../reducers/store';
import * as Utils from '../utils';

import styles from './index.module.css';
import defaultStyles from '../../../../styles/formSetStyles.module.css';

import {
  AutocompleteAsyncInput,
  Suggestion,
} from '../../../../components/AutocompleteAsyncInput';
import { Button } from '../../../../components/Button';
import { GroupInputs } from '../../../../components/GroupInputs';
import { InfoMessage } from '../../../../components/InfoMessage';
import LabeledInput from '../../../../components/LabeledInput';
import { SelectInput } from '../../../../components/SelectInput';
import { TextInput } from '../../../../components/TextInput';

///////////////////////////////////////////////////
////// TYPES

interface StateProps {
  patient: api.Patient.Patient;
}

export interface ProcedureToAdd {
  procedure: api.Procedure.StandardProcedure;
  nameRaw: string | null;
  dateDay: number | null;
  dateMonth: number | null;
  dateYear: number;
  laterality: api.Procedure.Laterality | null;
}

interface Props extends WithTranslation, StateProps {
  selectedProcedure: api.Procedure.StandardProcedure | null;
  addProcedure: (procedureToAdd: ProcedureToAdd) => void;
  handleSelect: (
    suggestion: Suggestion<api.Procedure.StandardProcedure>,
  ) => void;
  getProcedureSuggestions: (
    input: string,
  ) => Promise<Suggestion<api.Procedure.StandardProcedure>[]>;
  initialText: string;
}

interface State {
  errorMessage?: string;
  nameRaw: string | null;
  dateDay: number | null;
  dateMonth: number | null;
  dateYear: number | null;
  laterality: api.Procedure.Laterality | null;
}

///////////////////////////////////////////////////
////// COMPONENT

class ModalBody extends React.Component<Props, State> {
  state: State = {
    nameRaw: null,
    dateDay: null,
    dateMonth: null,
    dateYear: null,
    laterality: null,
  };

  handleContinue = () => {
    const { patient, selectedProcedure, t } = this.props;
    const { nameRaw, dateDay, dateMonth, dateYear, laterality } = this.state;

    if (
      !selectedProcedure ||
      !dateYear ||
      !Utils.isValidProcedureToAdd({
        procedure: selectedProcedure,
        nameRaw,
        dateDay,
        dateMonth,
        dateYear,
        laterality,
      })
    ) {
      this.setState({
        errorMessage: t('Please provide more details regarding your procedure'),
      });
      return;
    }

    const surgeryDate = moment({
      year: dateYear,
      month: dateMonth ? dateMonth - 1 : 0,
      day: dateDay || 1,
    });
    const isValidDate = surgeryDate.isValid();
    const isSurgeryBeforeDateOfBirth = surgeryDate.isBefore(
      patient.dateOfBirth,
    );
    const isSurgeryAfterToday = surgeryDate.isAfter(moment());
    if (!isValidDate || isSurgeryBeforeDateOfBirth || isSurgeryAfterToday) {
      this.setState({
        errorMessage: t(
          `Please enter a valid date between your birth date and today's date.`,
        ),
      });
      return;
    }

    this.setState({ errorMessage: '' });
    this.props.addProcedure({
      procedure: selectedProcedure,
      nameRaw: nameRaw,
      dateDay: dateDay,
      dateMonth: dateMonth,
      dateYear: dateYear,
      laterality: laterality,
    });
  };

  render() {
    const { selectedProcedure, handleSelect, getProcedureSuggestions, t } =
      this.props;
    const { nameRaw, dateDay, dateMonth, dateYear, laterality, errorMessage } =
      this.state;

    if (selectedProcedure) {
      const lateralityOptions = Object.values(api.Procedure.Laterality).map(
        (laterality) => ({
          display: t(Utils.LateralityPrettyMap[laterality]),
          key: laterality,
          value: laterality,
        }),
      );
      const monthOptions = Utils.MonthList.map((month) => ({
        display: Utils.splitAndUpperFirst(month.name),
        key: month.name,
        value: month.index,
      }));
      const isOther = selectedProcedure.name === 'OTHER';

      return (
        <div>
          {errorMessage ? (
            <div className={styles.InfoMessage}>
              <InfoMessage isError={true}>{errorMessage}</InfoMessage>
            </div>
          ) : null}
          <GroupInputs title={selectedProcedure.displayName}>
            {isOther ? (
              <></>
            ) : (
              <div className={styles.Label}>{t('When was your surgery')}</div>
            )}
            {isOther && (
              <div className={styles.PaddingBottom}>
                <TextInput
                  className={styles.PaddingBottom}
                  id="procedure-modal-name"
                  defaultValue={nameRaw || undefined}
                  onChange={(e) =>
                    this.setState({ nameRaw: e.target.value || null })
                  }
                  placeholder={t('Name of surgery')}
                />
              </div>
            )}
            <div className={styles.PaddingBottom}>
              <SelectInput
                id="procedure-modal-dateMonth"
                value={dateMonth || undefined}
                onChange={(dateMonth: number) => {
                  this.setState({ dateMonth: dateMonth });
                }}
                options={monthOptions}
                placeholder={t('Month')}
                isOptional={true}
                style={
                  dateMonth ? {} : { color: 'var(--color-input-placeholder)' }
                }
              />
            </div>
            <div className={styles.PaddingBottom}>
              <TextInput
                id="procedure-modal-dateDay"
                placeholder={t('Day (Optional)')}
                defaultValue={dateDay || undefined}
                type="number"
                onChange={(e) =>
                  this.setState({
                    dateDay: (e.target.value && +e.target.value) || null,
                  })
                }
              />
            </div>
            <div className={styles.PaddingBottom}>
              <TextInput
                id="procedure-modal-dateYear"
                placeholder={t('Year')}
                defaultValue={dateYear || undefined}
                type="number"
                onChange={(e) => this.setState({ dateYear: +e.target.value })}
              />
            </div>
            {selectedProcedure.requireLaterality && (
              <div className={styles.PaddingTop}>
                <LabeledInput
                  label={t('Side of body (Optional)')}
                  htmlFor="procedure-modal-laterality"
                >
                  <SelectInput
                    id="procedure-modal-laterality"
                    value={laterality || undefined}
                    onChange={(laterality: api.Procedure.Laterality) => {
                      this.setState({ laterality: laterality });
                    }}
                    options={lateralityOptions}
                    style={
                      laterality
                        ? {}
                        : { color: 'var(--color-input-placeholder)' }
                    }
                  />
                </LabeledInput>
              </div>
            )}
          </GroupInputs>
          <div className={defaultStyles.ButtonSubmitWrapper}>
            <Button
              type="button"
              children={t('Continue')}
              onClick={this.handleContinue}
            />
          </div>
        </div>
      );
    }

    return (
      <AutocompleteAsyncInput
        onSelect={(selected: Suggestion<api.Procedure.StandardProcedure>) =>
          handleSelect(selected)
        }
        getSuggestions={getProcedureSuggestions}
        placeholder={t(Utils.SearchPlaceholder)}
        icon={<SearchIcon className={styles.SearchIcon} />}
        initialString={this.props.initialText}
      />
    );
  }
}

function mapStateToProps(store: store.FormSetSelected): StateProps {
  return { patient: store.loggedInPatient };
}

export default connect(mapStateToProps)(withTranslation()(ModalBody));
